import React, { useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import DatePickerPopup from './DatePickerPopup';

interface DatePickerState {
  isOpen: boolean;
  defaultDate?:string
  x: number;
  y: number;
  onDateSelect: ((date: string,ifCloseDateDialog: boolean) => void) | null;
  currentDate?: string; // 添加当前日期状态
}

class DatePickerManager {
  private static instance: DatePickerManager;
  private container: HTMLDivElement | null = null;
  private setState: React.Dispatch<React.SetStateAction<DatePickerState>> | null = null;
  private resolvePromise: ((value: string) => void) | null = null; // 保存 resolve 函数

  static getInstance(): DatePickerManager {
    if (!DatePickerManager.instance) {
      DatePickerManager.instance = new DatePickerManager();
    }
    return DatePickerManager.instance;
  }

  init(setState: React.Dispatch<React.SetStateAction<DatePickerState>>) {
    this.setState = setState;
    if (!this.container) {
      this.container = document.createElement('div');
      document.body.appendChild(this.container);
    }
  }

  // 将时间戳格式的日期转换为 YYYY-MM-DD 格式
  private timestampToDateString(timestamp?: string): string | undefined {
    if (!timestamp || timestamp === '-1' || timestamp === '0') return undefined;
    const num = Number(timestamp);
    if (isNaN(num)) return undefined;
    const date = new Date(num);
    if (isNaN(date.getTime())) return undefined;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  open(x: number, y: number,onDateSelect: (date: string, ifCloseDateDialog: boolean) => void, defaultDate?: string): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.setState) {
        reject(new Error('DatePickerManager not initialized'));
        return;
      }

      // 保存 resolve 函数以便在关闭时使用
      this.resolvePromise = resolve;

      // 调整位置，确保弹窗不超出屏幕边界
      const adjustedX = Math.min(x, window.innerWidth - 320); // 300 + 20 margin
      const adjustedY = Math.min(y, window.innerHeight - 320); // 假设日历高度约300px

      // 将时间戳格式的 defaultDate 转换为 YYYY-MM-DD 格式，确保关闭时返回的格式一致
      const initialDateString = this.timestampToDateString(defaultDate);


      this.setState({
        isOpen: true,
        x: adjustedX,
        y: adjustedY,
        currentDate: initialDateString, // 初始化当前日期
        ...(defaultDate && { defaultDate }),
        onDateSelect: (date: string, ifCloseDateDialog: boolean) => {
          if (ifCloseDateDialog) {
            resolve(date);
            this.close();
          } else {
            // 更新当前日期但不关闭弹窗
            this.updateCurrentDate(date);
          }
        }
      });
    });
  }

  updateCurrentDate(date: string) {
    if (this.setState) {
      this.setState(prev => ({ ...prev, currentDate: date }));
    }
  }

  close(returnCurrentDate: boolean = false) {
    // 先保存 resolve 引用，避免异步 setState 回调中 resolvePromise 已被清空
    const resolve = this.resolvePromise;
    this.resolvePromise = null;

    if (this.setState) {
      this.setState(prev => {
        // 如果需要返回当前日期且有 resolve 函数
        if (returnCurrentDate && resolve && prev.currentDate) {
          resolve(prev.currentDate);
        }
        return { ...prev, isOpen: false };
      });
    }
  }


  destroy() {
    if (this.container) {
      document.body.removeChild(this.container);
      this.container = null;
      this.resolvePromise = null;
    }
  }
}

const DatePickerProvider: React.FC = () => {
  const [state, setState] = useState<DatePickerState>({
    isOpen: false,
    x: 0,
    y: 0,
    onDateSelect: null
  });

  React.useEffect(() => {
    const manager = DatePickerManager.getInstance();
    manager.init(setState);

    return () => {
      manager.destroy();
    };
  }, []);


  const handleClose = useCallback(() => {
    const manager = DatePickerManager.getInstance();
    manager.close(true); // 传入 true 表示要返回当前日期
  }, []);

  const handleDateSelect = useCallback((date: string, ifCloseDateDialog: boolean) => {
    if (state.onDateSelect) {
      state.onDateSelect(date, ifCloseDateDialog);
    }
  }, [state.onDateSelect]);

  if (!state.isOpen) return null;

  return (
    <DatePickerPopup
      x={state.x}
      y={state.y}
      defaultDate={state?.defaultDate}
      onDateSelect={handleDateSelect}
      onClose={handleClose}
    />
  );
};

// 导出单例实例和组件
export const DatePicker = DatePickerManager.getInstance();
export default DatePickerProvider;